import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";
/**
 * The `componentType` catalog for `DscrValueSource`, mirroring
 * `pricing_structures.DSCR_COMPONENTS` — `(bucket, annual)` per type.
 *
 * The server is the source of truth (`GET /pricing/dscr_calc_components`, which
 * returns `{componentType, bucket, annual}`); this is the offline fallback so
 * the Add-Source drawer still works if that call fails. Strings must match
 * verbatim — they're stored as the human-readable value, the same convention
 * `LIQUIDITY_SOURCE_TYPES` uses.
 *
 * BUCKET drives the math: income feeds the DSCR numerator, expense joins P&I in
 * PITIA. ANNUAL drives normalisation — the server divides those by 12 into
 * `monthlyAmount`. Neither is recomputed here.
 */
export const DSCR_COMPONENTS = [
  { componentType: "Gross Monthly Rent", bucket: "income", annual: false },
  { componentType: "Other Monthly Income", bucket: "income", annual: false },
  { componentType: "Annual Taxes", bucket: "expense", annual: true },
  { componentType: "Annual Insurance", bucket: "expense", annual: true },
  { componentType: "Annual Flood Insurance", bucket: "expense", annual: true },
  { componentType: "Annual HOA Fees", bucket: "expense", annual: true },
  { componentType: "Annual Mgmt Fees", bucket: "expense", annual: true },
  // 5-9 unit operating expenses (Guidelines v8.0) — ANNUAL, ÷12 into PITIA like
  // every other Annual row. Added 2026-10-14 (EV31101): the MF figure used to be
  // typed into the monthly row below and counted every month.
  { componentType: "Annual Operating Expenses (5-9 Units)", bucket: "expense", annual: true },
  // A genuinely MONTHLY item — never the multifamily operating expense.
  { componentType: "Other Monthly Expense", bucket: "expense", annual: false },
];

export const ANNUAL_OPERATING_EXPENSES_5_9 = "Annual Operating Expenses (5-9 Units)";

/**
 * Units a `propertyType` encodes — `UNITS_BY_PROPERTY_TYPE` in the backend's
 * pipeline.py, verbatim. Multifamily / Mixed-Use / Commercial / Other carry no
 * count, so the property's own units decide.
 */
const UNITS_BY_PROPERTY_TYPE = {
  SFR: 1,
  Condo: 1,
  Townhome: 1,
  "Planned Unit Development (PUD)": 1,
  "Non-Warrantable Condo": 1,
  "2-unit": 2,
  "3-unit": 3,
  "4-unit": 4,
  "5-unit": 5,
  "6-unit": 6,
  "7-unit": 7,
  "8-unit": 8,
};

/** The backend's `SubjectProperty.unitCount`: units[] first, then `numberOfUnits`, then the type. */
const propertyUnitCount = (sp) => {
  if ((sp?.units || []).length) return sp.units.length;
  if (sp?.numberOfUnits != null && sp.numberOfUnits !== "") return Number(sp.numberOfUnits);
  return UNITS_BY_PROPERTY_TYPE[sp?.propertyType] ?? null;
};

/** True when any subject property on the loan is 5–9 units. */
export const loanHasFiveToNineUnits = (loan) =>
  (loan?.subjectProperties || []).some((sp) => {
    const n = propertyUnitCount(sp);
    return n != null && n >= 5 && n <= 9;
  });

const FALLBACK = new Map(DSCR_COMPONENTS.map((c) => [c.componentType, c]));

/** Component metadata by type — the server catalog first, the fallback second. */
export const getComponentMeta = (componentType, serverComponents) => {
  const fromServer = (serverComponents || []).find(
    (c) => c?.componentType === componentType,
  );
  const local = FALLBACK.get(componentType);
  return {
    componentType,
    bucket: fromServer?.bucket || local?.bucket || "expense",
    annual: fromServer?.annual ?? local?.annual ?? false,
  };
};

/**
 * Which table a row belongs in. `isIncome` is computed server-side on every
 * row — prefer it, and fall back to the catalog only for a row that hasn't
 * round-tripped yet (an unsaved add).
 */
export const isIncomeRow = (source, serverComponents) =>
  typeof source?.isIncome === "boolean"
    ? source.isIncome
    : getComponentMeta(source?.componentType, serverComponents).bucket ===
      "income";

/** The bucket a row lives in — `"income"` | `"expense"`. */
export const sourceBucket = (source, serverComponents) =>
  isIncomeRow(source, serverComponents) ? "income" : "expense";

/**
 * The catalog narrowed to ONE section's components — the Add-Source drawer
 * opened from Income sources lists only income types, from Expense sources
 * only expense types (owner feedback 2026-08-25: the combined dropdown reads
 * as data-entry risk). Server catalog first, the local list as fallback; no
 * bucket → the whole list (an edit whose row can't be placed).
 *
 * Pass the loan to put `Annual Operating Expenses (5-9 Units)` first on a
 * 5–9 unit loan (dscr-calculator-workbook.md, 2026-10-14) — it becomes the
 * drawer's default component there.
 */
export const componentsForBucket = (bucket, serverComponents, loan) => {
  const catalog =
    serverComponents && serverComponents.length
      ? serverComponents
      : DSCR_COMPONENTS;
  const list = bucket ? catalog.filter((c) => c?.bucket === bucket) : catalog;
  if (bucket === "income" || !loan || !loanHasFiveToNineUnits(loan)) return list;
  const opex = list.find((c) => c?.componentType === ANNUAL_OPERATING_EXPENSES_5_9);
  return opex ? [opex, ...list.filter((c) => c !== opex)] : list;
};

export const GROSS_MONTHLY_RENT = "Gross Monthly Rent";
export const OTHER_MONTHLY_INCOME = "Other Monthly Income";

/**
 * `DscrValueSource.rentBasis` — values VERBATIM from the model's
 * `json_schema_extra.options` (`pricing_structures.py`, added 2026-09-15).
 * Which qualifying-rent basis a Gross Monthly Rent row is on:
 *   in_place — the lease rent (evidenced by the lease)
 *   market   — appraisal 1007/1025 market rent (the vacant-unit basis)
 *   str      — short-term-rental income, qualifies at MIN(income, 125% × market)
 * Defaulted from the unit; a UW change is a `changeHistory` row.
 */
export const RENT_BASIS = { IN_PLACE: "in_place", MARKET: "market", STR: "str" };
export const RENT_BASIS_OPTIONS = [
  { value: RENT_BASIS.IN_PLACE, label: "In-place" },
  { value: RENT_BASIS.MARKET, label: "Market" },
  { value: RENT_BASIS.STR, label: "STR" },
];
export const rentBasisLabel = (basis) =>
  RENT_BASIS_OPTIONS.find((o) => o.value === basis)?.label || null;

const num = (v) => (v == null || v === "" ? null : Number(v));

/**
 * Every unit on the loan, flattened with its property, in the shape the
 * income drawer's unit picker and the table's Source cell read. The rent
 * candidates come from the unit's own fields (`unitMonthlyInPlaceRent` /
 * `unitMonthlyMarketRent` / `airbnbIncome`) — the atoms the workbook rolls up.
 */
export const listLoanUnits = (loan) => {
  const out = [];
  (loan?.subjectProperties || []).forEach((sp, pi) => {
    const propertyLabel =
      addressLabel(sp?.address) ||
      sp?.address?.street ||
      sp?.propertyName ||
      `Property ${pi + 1}`;
    (sp?.units || []).forEach((u, ui) => {
      out.push({
        unitId: u?._id,
        propertyId: sp?._id,
        propertyLabel,
        unitNumber: u?.unitNumber || String(ui + 1),
        leaseType: u?.leaseType || null,
        unitVacant: !!u?.unitVacant,
        inPlaceRent: num(u?.unitMonthlyInPlaceRent),
        marketRent: num(u?.unitMonthlyMarketRent),
        strIncome: num(u?.airbnbIncome),
      });
    });
  });
  return out;
};

/**
 * Property types that are one unit by definition (`unit_counts` in the
 * backend's pipeline.py) — `propertyType` options, verbatim. A property of
 * one of these types with no units yet doesn't make UW go create "Unit 1" on
 * the Properties tab first: the rent drawers offer a placeholder unit and
 * create the real one on save (`useMaterializeUnit`).
 */
export const SINGLE_UNIT_PROPERTY_TYPES = ["SFR", "Townhome", "Condo"];

const PLACEHOLDER_UNIT_PREFIX = "auto:";

export const isPlaceholderUnitId = (unitId) =>
  typeof unitId === "string" && unitId.startsWith(PLACEHOLDER_UNIT_PREFIX);

/**
 * `listLoanUnits` plus one placeholder unit for every single-unit property
 * (SFR / Townhome / Condo) that has no units. Placeholders carry
 * `isPlaceholder: true` and a `unitId` of `auto:<propertyId>` — never persist
 * that id; resolve it through `useMaterializeUnit` first.
 */
export const listLoanUnitsWithDefaults = (loan) => {
  const out = listLoanUnits(loan);
  (loan?.subjectProperties || []).forEach((sp, pi) => {
    if (!sp?._id || (sp?.units || []).length) return;
    if (!SINGLE_UNIT_PROPERTY_TYPES.includes(sp?.propertyType)) return;
    out.push({
      unitId: `${PLACEHOLDER_UNIT_PREFIX}${sp._id}`,
      propertyId: sp._id,
      propertyLabel: propertyLabelOf(sp, pi),
      unitNumber: "1",
      leaseType: null,
      unitVacant: false,
      inPlaceRent: null,
      marketRent: null,
      strIncome: null,
      isPlaceholder: true,
    });
  });
  return out;
};

export const findLoanUnit = (loan, unitId) =>
  unitId ? listLoanUnits(loan).find((u) => String(u.unitId) === String(unitId)) : null;

export const findLoanProperty = (loan, propertyId) =>
  propertyId
    ? (loan?.subjectProperties || []).find(
        (sp) => String(sp?._id) === String(propertyId),
      )
    : null;

export const propertyLabelOf = (sp, index) =>
  addressLabel(sp?.address) ||
  sp?.address?.street ||
  sp?.propertyName ||
  `Property ${(index ?? 0) + 1}`;

/**
 * The basis the guideline picks from the unit's own state — the same rule
 * `build_dscr_calc` seeds with: STR lease with income → str; vacant or no
 * in-place rent → market; else in_place.
 */
export const defaultRentBasis = (unit) => {
  if (!unit) return RENT_BASIS.IN_PLACE;
  if (unit.leaseType === "Short-Term Rental" && unit.strIncome) return RENT_BASIS.STR;
  if (unit.unitVacant || !unit.inPlaceRent) return RENT_BASIS.MARKET;
  return RENT_BASIS.IN_PLACE;
};

/** The unit's candidate amount on a given basis (null when the unit lacks it). */
export const unitAmountForBasis = (unit, basis) => {
  if (!unit) return null;
  if (basis === RENT_BASIS.STR) return unit.strIncome ?? null;
  if (basis === RENT_BASIS.MARKET) return unit.marketRent ?? null;
  return unit.inPlaceRent ?? null;
};

/** "1551 SE 23rd Ave · Unit 2" — the derived label for a unit-tied rent row. */
export const unitRowLabel = (unit) =>
  unit ? `${unit.propertyLabel} · Unit ${unit.unitNumber}` : "";

/**
 * Lease above market: an in-place row qualifies at the lease amount but
 * carries the "two months of rent payments verified" condition (EV DSCR v8.0
 * §Qualifying Monthly Rent). Amber chip on the row.
 */
export const leaseAboveMarket = (source, unit) =>
  source?.componentType === GROSS_MONTHLY_RENT &&
  (source?.rentBasis || RENT_BASIS.IN_PLACE) === RENT_BASIS.IN_PLACE &&
  unit?.marketRent != null &&
  Number(source?.amount) > Number(unit.marketRent);

/** Rows with no backing document — the "Missing" flag in the table. */
export const sourceHasEvidence = (source) =>
  Array.isArray(source?.loanDocumentIds) && source.loanDocumentIds.length > 0;

/**
 * Every table on this tab sits in a closed bordered block, the way the admin
 * screens' grids do (`PricingGrid` / `AdminCard`) — a hairline box with a radius
 * and its corners clipped, so a table reads as one object rather than rows
 * floating on the card. The block (and the grid look inside it) is shared with
 * the Liquidity Calc tab — see `LoanDrilldownScreen/shared/cardGrid.js`.
 *
 * The grid inside must render WITHOUT its own border (`noBorder` on
 * `AgGridTableWrapper`), or the two stack into a doubled edge — the same bug the
 * Banned Appraisers card hit.
 */
export { CARD_TABLE_BOX_SX as TABLE_BOX_SX } from "../../shared/cardGrid";
